/**
 * Calcula la posición óptima para el popover en relación al elemento destacado
 */
export function calculatePosition (
  targetElement: HTMLElement,
  popover: HTMLElement,
  placement: 'top' | 'right' | 'bottom' | 'left' | 'center' = 'bottom',
  offset = { x: 0, y: 0 }
): { top: number, left: number } {
  // Si no hay elemento objetivo, posicionar en el centro de la pantalla
  if (!targetElement) {
    return {
      top: window.innerHeight / 2 - popover.offsetHeight / 2 + offset.y,
      left: window.innerWidth / 2 - popover.offsetWidth / 2 + offset.x,
    }
  }

  const targetRect = targetElement.getBoundingClientRect()
  const popoverRect = popover.getBoundingClientRect()

  // Calcular posición basado en la colocación
  let top = 0
  let left = 0

  switch (placement) {
    case 'top':
      top = targetRect.top - popoverRect.height - 10
      left = targetRect.left + targetRect.width / 2 - popoverRect.width / 2
      break
    case 'right':
      top = targetRect.top + targetRect.height / 2 - popoverRect.height / 2
      left = targetRect.right + 10
      break
    case 'bottom':
      top = targetRect.bottom + 10
      left = targetRect.left + targetRect.width / 2 - popoverRect.width / 2
      break
    case 'left':
      top = targetRect.top + targetRect.height / 2 - popoverRect.height / 2
      left = targetRect.left - popoverRect.width - 10
      break
    case 'center':
      top = targetRect.top + targetRect.height / 2 - popoverRect.height / 2
      left = targetRect.left + targetRect.width / 2 - popoverRect.width / 2
      break
  }

  // Aplicar offset
  top += offset.y
  left += offset.x

  // Asegurar que el popover no se salga de la pantalla
  if (left < 10) left = 10
  if (left + popoverRect.width > window.innerWidth - 10) {
    left = window.innerWidth - popoverRect.width - 10
  }

  if (top < 10) top = 10
  if (top + popoverRect.height > window.innerHeight - 10) {
    top = window.innerHeight - popoverRect.height - 10
  }

  // Incluir el scroll de la página
  top += window.pageYOffset
  left += window.pageXOffset

  return { top, left }
}

/**
 * Hace scroll al elemento destacado
 */
export function scrollToElement (element: HTMLElement | null, smooth = true): void {
  if (!element) return

  const rect = element.getBoundingClientRect()
  const isInViewport = (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= window.innerHeight &&
    rect.right <= window.innerWidth
  )

  if (!isInViewport) {
    const scrollOptions: ScrollIntoViewOptions = {
      behavior: smooth ? 'smooth' : 'auto',
      block: 'center',
    }

    element.scrollIntoView(scrollOptions)
  }
}

/**
 * Crea un objeto para almacenar datos del tour en localStorage
 */
export function saveTourState (tourId: string, completed = false): void {
  try {
    const tourHistory = JSON.parse(localStorage.getItem('tourHistory') || '{}')
    tourHistory[tourId] = {
      completed,
      timestamp: new Date().toISOString(),
    }
    localStorage.setItem('tourHistory', JSON.stringify(tourHistory))
  } catch (error) {
    console.error('Error al guardar el estado del tour:', error)
  }
}

/**
 * Obtiene el historial de tours desde localStorage
 */
export function getTourHistory (): Record<string, { completed: boolean, timestamp: string }> {
  try {
    return JSON.parse(localStorage.getItem('tourHistory') || '{}')
  } catch (error) {
    console.error('Error al obtener el historial de tours:', error)
    return {}
  }
}

/**
 * Verifica si un tour específico ha sido completado
 */
export function isTourCompleted (tourId: string): boolean {
  const history = getTourHistory()
  return !!history[tourId]?.completed
}

/**
 * Anima una propiedad CSS
 */
export function animateCSS (
  element: HTMLElement,
  property: string,
  fromValue: string,
  toValue: string,
  duration = 300
): Promise<void> {
  return new Promise(resolve => {
    if (!element) {
      resolve()
      return
    }

    element.style.transition = `${property} ${duration}ms ease-in-out`
    element.style[property as any] = fromValue

    // Force reflow
    // eslint-disable-next-line no-unused-expressions
    element.offsetHeight

    element.style[property as any] = toValue

    const onTransitionEnd = () => {
      element.removeEventListener('transitionend', onTransitionEnd)
      resolve()
    }

    element.addEventListener('transitionend', onTransitionEnd)

    // Failsafe in case the transitionend event doesn't fire
    setTimeout(resolve, duration + 50)
  })
}
